import React, { useState, useEffect } from "react";
import { useKlinischerBefund } from "../../context/KlinischerBefundContext";
import { useFussstatus } from "../../context/FussstatusContext";
import { useFussstatusGrafik } from "../../context/FussstatusGrafikContext";
import { useKategorisierung } from "../../context/KategorisierungContext";
import { useAllgemeineDaten } from "../../context/AllgemeineDatenContext";
import { useNavigate } from "react-router-dom";
import kategorisierungGrafik from "../../assets/kategorisierungGrafik.png"
import styles from "./kategorisierung.module.css";
import { RECIPES, getSubVariantKey as getSubVariantKeyBase } from "../../utils/rezeptvorschlagUtils";

const Kategorisierung = () => {
  const navigate = useNavigate();
  const [showGrafik, setShowGrafik] = useState(false);
  const [showZusatzLinks, setShowZusatzLinks] = useState(false);
  const [showZusatzRechts, setShowZusatzRechts] = useState(false);

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  
  const { data: klinischerBefund = {} } = useKlinischerBefund();
  const { data: fussstatus = {} } = useFussstatus();
  const { data: fussstatusGrafik = {} } = useFussstatusGrafik();
  const { setData: setKategorisierungData } = useKategorisierung();

  const { data: allgemeineData } = useAllgemeineDaten();
  const fussAuswahl = allgemeineData?.fussAuswahl;
    
  const showLeftFoot = fussAuswahl !== "rechts";
  const showRightFoot = fussAuswahl !== "links";
  const showBothFeet = showLeftFoot && showRightFoot;

  const {
    linksHinweisPNP = "", rechtsHinweisPNP = "",
    linksHinweisPAVK = "", rechtsHinweisPAVK = "",
    linksMikrofilament = "", rechtsMikrofilament = "",
    linksVibrationsempfindung = "", rechtsVibrationsempfindung = "",
  } = klinischerBefund;

  const {
    linksUlcus = "", rechtsUlcus = "",
    linksUlcusTyp = "", rechtsUlcusTyp = "",
    linksZustandNachAmputation = "", rechtsZustandNachAmputation = "",
    linksZustandNachZehenamputation = "", rechtsZustandNachZehenamputation = "",
    linksHinweisCNO = "", rechtsHinweisCNO = "",
    linksHinweisCNOTyp = "", rechtsHinweisCNOTyp = "",
    linksFussdeformitaeten = "", rechtsFussdeformitaeten = "",
    linksPedographischerBefund = "", rechtsPedographischerBefund = "",
    dialysepflichtigeNiereninsuffienz  = "",
    linksDrohendeDorsaleUlcera = "", rechtsDrohendeDorsaleUlcera = "",
    linksPraeulzerativeVeranderungen = "", rechtsPraeulzerativeVeranderungen = "",
  } = fussstatus;

  const {
    plLeft = [], plRight = [],
    dorsLeft = [], dorsRight = [],
    medLeft = [], medRight = [],
    latLeft = [], latRight = [],
  } = fussstatusGrafik;

const getBaseType = (side) => {
  const isLeft = side === "links";
  const PNP   = isLeft ? linksHinweisPNP   : rechtsHinweisPNP;
  const PAVK  = isLeft ? linksHinweisPAVK  : rechtsHinweisPAVK;
  const vib   = isLeft ? linksVibrationsempfindung : rechtsVibrationsempfindung;
  const mikro = isLeft ? linksMikrofilament : rechtsMikrofilament;
  const ulcus     = isLeft ? linksUlcus     : rechtsUlcus;
  const ulcusTyp  = isLeft ? linksUlcusTyp  : rechtsUlcusTyp;
  const deform    = isLeft ? linksFussdeformitaeten : rechtsFussdeformitaeten;
  const pedo      = isLeft ? linksPedographischerBefund : rechtsPedographischerBefund;
  const postAmp   = isLeft ? linksZustandNachAmputation : rechtsZustandNachAmputation;
  const postZehe  = isLeft ? linksZustandNachZehenamputation : rechtsZustandNachZehenamputation;
  const cno       = isLeft ? linksHinweisCNO : rechtsHinweisCNO;
  const cnoTyp    = isLeft ? linksHinweisCNOTyp : rechtsHinweisCNOTyp;
  const pl   = isLeft ? plLeft   : plRight;
  const dors = isLeft ? dorsLeft : dorsRight;
  const med  = isLeft ? medLeft  : medRight;
  const lat  = isLeft ? latLeft  : latRight;

  const hasPNPorPAVK = PNP === "ja" || PAVK === "ja";

  const safeIncludes = (value, search) => {
    if (!value || typeof value !== "string") return false;
    return value.includes(search);
  };

  // Typ VIId: Ferse + akut (also counts when "abgeheilt/akut" because akut is present)
  if (ulcus === "ja" && safeIncludes(ulcusTyp, "akut") &&
      pl.some(f => f.loc === "Ferse" && f.befund === "Ulcus akut") &&
      hasPNPorPAVK) return "Typ VIId";

  // Typ VIIc: plantar not Ferse + akut (also counts when "abgeheilt/akut")
  if (ulcus === "ja" && safeIncludes(ulcusTyp, "akut") &&
      pl.some(f => f.befund === "Ulcus akut" && f.loc !== "Ferse") &&
      hasPNPorPAVK) return "Typ VIIc";

  if ((ulcus === "nein" || (ulcus === "ja" && ulcusTyp === "abgeheilt")) &&
      cno === "ja" && cnoTyp === "floride/aktiv" &&
      hasPNPorPAVK) return "Typ VIIb";

  // Typ VIIa: medial/lateral/dorsal + akut (also counts when "abgeheilt/akut")
  if (ulcus === "ja" && safeIncludes(ulcusTyp, "akut") &&
      (med.some(f => f.befund === "Ulcus akut") ||
      lat.some(f => f.befund === "Ulcus akut") ||
      dors.some(f => f.befund === "Ulcus akut")) &&
      hasPNPorPAVK) return "Typ VIIa";

    // Typ VI: post-amputation → must NOT have active ulcer ("akut" or "abgeheilt/akut")
  if (postAmp === "ja" && hasPNPorPAVK &&
      !safeIncludes(ulcusTyp, "akut")) return "Typ VI";

    // Typ V: CNO inaktiv → must NOT have active ulcer ("akut" or "abgeheilt/akut")
  if (cno === "ja" && cnoTyp === "inaktiv" && hasPNPorPAVK &&
      !safeIncludes(ulcusTyp, "akut")) return "Typ V";

  // Typ IV: deform + PNP/PAVK, no active ulcer, no CNO
  if ((deform === "ja" || deform === "hochgradig") && hasPNPorPAVK &&
      !safeIncludes(ulcusTyp, "akut") &&
      cno !== "ja") return "Typ IV";

  // Typ III: abgeheilt + other risk factors → only pure "abgeheilt" allowed (NOT "abgeheilt/akut")
  if (ulcus === "ja" && !safeIncludes(ulcusTyp, "akut") && hasPNPorPAVK &&
      (deform === "ja" ||
      pl.some(f => f.loc === "M1" && f.befund === "Hallux valgus") ||
      dors.some(f => f.loc === "M1" && f.befund === "Hallux valgus") ||
      pedo === "auffällig" ||
      postZehe === "ja")) return "Typ III";

  // Typ IIb — check for Zehenamputation upgrade
  if (ulcus === "nein" && hasPNPorPAVK &&
      (dialysepflichtigeNiereninsuffienz === "ja" ||
       (isLeft ? linksDrohendeDorsaleUlcera : rechtsDrohendeDorsaleUlcera) === "ja" ||
       (isLeft ? linksPraeulzerativeVeranderungen : rechtsPraeulzerativeVeranderungen) === "ja" ||
       pl.length > 0 || dors.length > 0)) {
    if (postZehe === "ja") {
      return "Typ III";
    }
    return "Typ IIb";
  }
  // Typ IIa — check for Zehenamputation 
  if (ulcus === "nein" && hasPNPorPAVK &&
      ((["0","1","2","3"].includes(vib) && mikro === "nein") || PAVK === "ja")) {
    if (postZehe === "ja") {
      return "Typ III";
    }
    return "Typ IIa";
  }
  // Typ I: no PNP/PAVK + conditions → requires ulcus = "nein"
  if (!hasPNPorPAVK && ulcus === "nein" &&
      ((deform === "ja" && ["4","5","6","7","8"].includes(vib) && mikro === "ja") ||
      deform === "hochgradig" ||
      pedo === "extreme Druckspritzen")) return "Typ I";

  // Typ 0: no PNP/PAVK + conditions → requires ulcus = "nein"
  if (!hasPNPorPAVK && ulcus === "nein" &&
      ["5","6","7","8"].includes(vib) &&
      mikro === "ja" &&
      deform === "nein" &&
      ["unauffällig", "auffällig"].includes(pedo)) return "Typ 0";

  return "Kein Typ";
};

  const finalLinks  = getBaseType("links");
  const finalRechts = getBaseType("rechts");

  const getSubVariantKey = (typ, side) =>
    getSubVariantKeyBase(typ, fussstatus, fussstatusGrafik, side);

  const renderRecipe = (subKey, isPair) => {
    const recipe = RECIPES[subKey];
    if (!recipe) return null;
    const text = isPair ? recipe.pair : recipe.single;
    return (
      <>
        <p className={styles["recipe-subtype"]}>{subKey}</p>
        <p className={styles["recipe-suggestion"]}>
          {text ?? <em>Rezeptvorschlag wird noch ergänzt.</em>}
        </p>
      </>
    );
  };

  const subKeyLinks  = getSubVariantKey(finalLinks,  "links");
  const subKeyRechts = getSubVariantKey(finalRechts, "rechts");
  const sameType = showBothFeet && subKeyLinks === subKeyRechts;

  React.useEffect(() => {
    setKategorisierungData({
      linksRisikotyp: finalLinks,
      rechtsRisikotyp: finalRechts,
    });
  }, [finalLinks, finalRechts, setKategorisierungData]);

  const renderFoot = (label, typ, side) => {
  const isLeft = side === "links";
  const prefix = isLeft ? "links" : "rechts";
  const pl   = isLeft ? plLeft   : plRight;
  const dors = isLeft ? dorsLeft : dorsRight;
  const med  = isLeft ? medLeft  : medRight;
  const lat  = isLeft ? latLeft  : latRight;
  const pedo = fussstatus[`${prefix}PedographischerBefund`] || "";

  return (
    <div className={styles["foot-section"]}>
      <h3 className={styles.footTitle}>{label}</h3>
      <p className={styles.riskGroup}>
        Risikogruppe: <strong>{typ}</strong>
      </p>
      {typ === "Kein Typ" && (
        <p className={styles.noTypeWarning}>
          Bitte besprechen Sie Ihre aktuelle Situation mit dem behandelnden Arzt.
        </p>
      )}
      <ul>
        {klinischerBefund[`${prefix}HinweisPNP`] !== "nein" && (
          <li><span className={styles.key}>PNP:</span> {klinischerBefund[`${prefix}HinweisPNP`] || "—"}</li>
        )}
        {klinischerBefund[`${prefix}HinweisPAVK`] !== "nein" && (
          <li><span className={styles.key}>PAVK:</span> {klinischerBefund[`${prefix}HinweisPAVK`] || "—"}</li>
        )}
        {klinischerBefund[`${prefix}Mikrofilament`] !== "nein" && (
          <li><span className={styles.key}>Mikrofilament:</span> {klinischerBefund[`${prefix}Mikrofilament`] || "—"}</li>
        )}
        {klinischerBefund[`${prefix}Vibrationsempfindung`] !== "nein" && (
          <li><span className={styles.key}>Vibration:</span> {klinischerBefund[`${prefix}Vibrationsempfindung`] || "—"}/8</li>
        )}
        {(fussstatus[`${prefix}Ulcus`] !== "nein") && (
          <li>
            <span className={styles.key}>Ulcus:</span>{" "}
            {fussstatus[`${prefix}Ulcus`] || "—"}{" "}
            {fussstatus[`${prefix}UlcusTyp`] && `(${fussstatus[`${prefix}UlcusTyp`]})`}
          </li>
        )}
        {(fussstatus[`${prefix}HinweisCNO`] !== "nein") && (
          <li>
            <span className={styles.key}>CNO:</span>{" "}
            {fussstatus[`${prefix}HinweisCNO`] || "—"}{" "}
            {fussstatus[`${prefix}HinweisCNOTyp`] && `(${fussstatus[`${prefix}HinweisCNOTyp`]})`}
          </li>
        )}
        {fussstatus[`${prefix}Fussdeformitaeten`] !== "nein" && (
          <li><span className={styles.key}>Deformitäten:</span> {fussstatus[`${prefix}Fussdeformitaeten`] || "—"}</li>
        )}
        {pedo !== "nein" && pedo && (
          <li><span className={styles.key}>Pedographisch:</span> {pedo}</li>
        )}
        {fussstatus[`${prefix}ZustandNachAmputation`] !== "nein" && (
          <li><span className={styles.key}>Z.n. (mind. transmetatarsaler) Amputation:</span> {fussstatus[`${prefix}ZustandNachAmputation`] || "—"}</li>
        )}
        {fussstatus[`${prefix}ZustandNachZehenamputation`] !== "nein" && (
          <li><span className={styles.key}>Z.n. Zehenamputation:</span> {fussstatus[`${prefix}ZustandNachZehenamputation`] || "—"}</li>
        )}
        {dialysepflichtigeNiereninsuffienz !== "nein" && (
          <li>
            <span className={styles.key}>Dialysepflichtige Niereninsuffizienz:</span>{" "}
            {dialysepflichtigeNiereninsuffienz}
          </li>
        )}
        {fussstatus[`${prefix}DrohendeDorsaleUlcera`] !== "nein" && (
          <li><span className={styles.key}>Drohende dorsale Ulcera:</span> {fussstatus[`${prefix}DrohendeDorsaleUlcera`] || "—"}</li>
        )}
        {fussstatus[`${prefix}PraeulzerativeVeranderungen`] !== "nein" && (
          <li><span className={styles.key}>Präulzerative Veränder.:</span> {fussstatus[`${prefix}PraeulzerativeVeranderungen`] || "—"}</li>
        )}

        {/* Grafik*/}
        {(pl.some(f => f.befund?.includes("Ulcus")) ||
          dors.some(f => f.befund?.includes("Ulcus")) ||
          med.some(f => f.befund?.includes("Ulcus")) ||
          lat.some(f => f.befund?.includes("Ulcus"))) && (
          <li><span className={styles.key}>Ulcus / auffällige Stellen:</span> ja</li>
        )}
        {(pl.some(f => f.loc === "M1" && f.befund === "Hallux valgus") ||
          dors.some(f => f.loc === "M1" && f.befund === "Hallux valgus")) && (
          <li><span className={styles.key}>Hallux valgus (M1):</span> ja</li>
        )}
        {(pl.length + dors.length) > 0 && (
          <li><span className={styles.key}>Irgendwelche plantar/dorsal Einträge:</span> ja</li>
        )}
        {pedo === "extreme Druckspritzen" && (
          <li><span className={styles.key}>Extreme Druckspritzen:</span> ja</li>
        )}

        {(pl.length + dors.length + med.length + lat.length > 0) && (
          <li className={styles.befundeSubtitle}>
            Befunde:
          </li>
        )}

        {/* Live list of all actual Befunde from grafik – grouped by view */}
        {["med", "lat", "pl", "dors"].map((view) => {
          const items = view === "med" ? med :
                        view === "lat" ? lat :
                        view === "pl" ? pl : dors;

          const viewName = view === "med" ? "Medial" :
                           view === "lat" ? "Lateral" :
                           view === "pl" ? "Plantar" : "Dorsal";

          if (items.length === 0) return null;

          return (
            <React.Fragment key={view}>
              <li style={{ marginTop: "1.2rem", fontWeight: "bold", fontSize: "1.05em" }}>
                {viewName}:
              </li>
              {items.map((f, idx) => (
                <li key={idx} style={{ marginLeft: "1.8rem", marginTop: "0.4rem" }}>
                  {f.loc} – {f.befund}
                </li>
              ))}
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
};

  const VIIdZusatzInfo = () => (
    <div className={styles["zusatz-block"]}>
      <p className={styles["zusatz-title"]}>Zusätze:</p>
      <ul className={styles["zusatz-list"]}>
        <li>Die Versorgung muss eine Kondylenführung und eine wirksame Reduktion axialer Kräfte gewährleisten.</li>
        <li>Konfektionierte Fersenentlastungsorthesen erfüllen laut DDG die erforderlichen Funktionsanforderungen nicht.</li>
        <li>Bei infizierter Ulzeration, AVK oder starker Exsudation nicht abnehmbare kniehohe Entlastungsvorrichtungen nur mit besonderer Vorsicht anwenden.</li>
        <li>Lokalisation, Seite, Ausmaß der Fersenläsion und erforderliche vollständige Druckentlastung auf der Verordnung dokumentieren.</li>
      </ul>
      <p className={styles["zusatz-alt-title"]}>Alternative Verordnungsmöglichkeiten nach Befund</p>
      <ul className={styles["zusatz-alt-list"]}>
        <li>Ein Total-Contact-Cast (TCC) zur vollständigen Entlastung einer plantaren Fersenläsion; Diagnose: diabetisches Fußsyndrom, Risikogruppe VII d.</li>
      </ul>
    </div>
  );

  const VIIcZusatzInfo = () => (
    <div className={styles["zusatz-block"]}>
      <p className={styles["zusatz-title"]}>Zusätze:</p>
      <ul className={styles["zusatz-list"]}>
        <li>Gewichtslimitierung und Herstellerangaben beachten.</li>
        <li>Passform muss sicher gewährleistet sein; andernfalls individuelle Orthese verordnen.</li>
        <li>Bei infizierter Ulzeration, AVK oder starker Exsudation nicht abnehmbare kniehohe Orthesen nur mit besonderer Vorsicht einsetzen; nach DDG sind abnehmbare Orthese beziehungsweise Verbandsschuh nachrangige Alternativen.</li>
        <li>Lokalisation und Seite der Läsion sowie gewünschte Entlastungszone auf dem Rezept beziehungsweise Verordnungsbogen ergänzen.</li>
      </ul>
      <p className={styles["zusatz-alt-title"]}>Alternative Verordnungsmöglichkeiten nach Befund</p>
      <ul className={styles["zusatz-alt-list"]}>
        <li>Eine abnehmbare kniehohe Orthese zur Druckentlastung bei plantarer Läsion (außer Ferse), individuell angepasst; Diagnose: diabetisches Fußsyndrom, Risikogruppe VII c.</li>
        <li>Ein individueller Interimsschuh mit gezielter plantarer Druckentlastung und diabetesadaptierter Fußbettung; Diagnose: diabetisches Fußsyndrom, Risikogruppe VII c.</li>
        <li>Ein Total-Contact-Cast (TCC) zur vollständigen Druckentlastung einer plantaren Läsion außerhalb der Ferse; Diagnose: diabetisches Fußsyndrom, Risikogruppe VII c.</li>
        <li>Ein Paar Verbandsschuhe mit ausreichendem Volumen für Wundverband und gezielter Druckentlastung der Ulkusregion; Diagnose: diabetisches Fußsyndrom, Risikogruppe VII c.</li>
      </ul>
    </div>
  );

  return (
    <div className={styles["kategorisierung-container"]}>
      <h1>Kategorisierung nach Risikogruppen</h1>
      <div className={`${styles["kategorisierung-grid"]} ${!showBothFeet ? styles['single-column'] : ''}`}>
        {showLeftFoot && (
          <div className={styles["foot-wrapper"]}>
            {renderFoot("Linker Fuß", finalLinks, "links")}
            {!sameType && subKeyLinks && (
              <div className={styles["foot-section"]}>
                <h3 className={styles.footTitle}>
                  Rezeptvorschlag{showBothFeet ? " – Linker Fuß" : ""}
                </h3>
                {renderRecipe(subKeyLinks, false)}
                {subKeyLinks === "Risikogruppe VIIc" && (
                  <>
                    <button type="button" className={`${styles["btn-primary"]} ${styles["zusatz-btn"]}`} onClick={() => setShowZusatzLinks(v => !v)}>
                      {showZusatzLinks ? "Zusatzinformation ausblenden" : "Zusatzinformation"}
                    </button>
                    {showZusatzLinks && <VIIcZusatzInfo />}
                  </>
                )}
                {subKeyLinks === "Risikogruppe VIId" && (
                  <>
                    <button type="button" className={`${styles["btn-primary"]} ${styles["zusatz-btn"]}`} onClick={() => setShowZusatzLinks(v => !v)}>
                      {showZusatzLinks ? "Zusatzinformation ausblenden" : "Zusatzinformation"}
                    </button>
                    {showZusatzLinks && <VIIdZusatzInfo />}
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {showRightFoot && (
          <div className={styles["foot-wrapper"]}>
            {renderFoot("Rechter Fuß", finalRechts, "rechts")}
            {!sameType && subKeyRechts && (
              <div className={styles["foot-section"]}>
                <h3 className={styles.footTitle}>
                  Rezeptvorschlag{showBothFeet ? " – Rechter Fuß" : ""}
                </h3>
                {renderRecipe(subKeyRechts, false)}
                {subKeyRechts === "Risikogruppe VIIc" && (
                  <>
                    <button type="button" className={`${styles["btn-primary"]} ${styles["zusatz-btn"]}`} onClick={() => setShowZusatzRechts(v => !v)}>
                      {showZusatzRechts ? "Zusatzinformation ausblenden" : "Zusatzinformation"}
                    </button>
                    {showZusatzRechts && <VIIcZusatzInfo />}
                  </>
                )}
                {subKeyRechts === "Risikogruppe VIId" && (
                  <>
                    <button type="button" className={`${styles["btn-primary"]} ${styles["zusatz-btn"]}`} onClick={() => setShowZusatzRechts(v => !v)}>
                      {showZusatzRechts ? "Zusatzinformation ausblenden" : "Zusatzinformation"}
                    </button>
                    {showZusatzRechts && <VIIdZusatzInfo />}
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {sameType && subKeyLinks && (
          <div className={`${styles["foot-section"]} ${styles["rezeptvorschlag-span"]}`}>
            <h3 className={styles.footTitle}>Rezeptvorschlag</h3>
            {renderRecipe(subKeyLinks, true)}
            {subKeyLinks === "Risikogruppe VIIc" && (
              <>
                <button type="button" className={`${styles["btn-primary"]} ${styles["zusatz-btn"]}`} onClick={() => setShowZusatzLinks(v => !v)}>
                  {showZusatzLinks ? "Zusatzinformation ausblenden" : "Zusatzinformation"}
                </button>
                {showZusatzLinks && <VIIcZusatzInfo />}
              </>
            )}
            {subKeyLinks === "Risikogruppe VIId" && (
              <>
                <button type="button" className={`${styles["btn-primary"]} ${styles["zusatz-btn"]}`} onClick={() => setShowZusatzLinks(v => !v)}>
                  {showZusatzLinks ? "Zusatzinformation ausblenden" : "Zusatzinformation"}
                </button>
                {showZusatzLinks && <VIIdZusatzInfo />}
              </>
            )}
          </div>
        )}
      </div>

      <div className={styles.imageContainer}>
        <p className={styles.grafikLabel}>Übersicht der Risikogruppen Kategorisierung</p>
        <button
          type="button"
          className={styles["btn-primary"]}
          onClick={() => setShowGrafik(prev => !prev)}
        >
          {showGrafik ? "Ausblenden" : "Anzeigen"}
        </button>
        {showGrafik && (
          <img
            src={kategorisierungGrafik}
            alt="Übersicht Risikogruppen / Kategorisierung"
            className={styles.kategorisierungGrafik}
          />
        )}
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles["btn-primary"]}
          onClick={() => navigate("/kriterien")}
        >
          Zurück
        </button>
        <button
          type="button"
          className={styles["btn-primary"]}
          onClick={() => navigate("/schuhversorgung")}
        >
          Weiter
        </button>
      </div>  
    </div>
  );
};

export default Kategorisierung;
